import React, { useEffect, useRef, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Icon } from '@iconify/react'
import { colors } from 'src/components/app-ui'

const useStyles = makeStyles({
  root: {
    position: 'relative',
    display: 'inline-flex',
  },
  trigger: {
    display: 'inline-flex',
    padding: 0,
    border: 0,
    background: 'transparent',
    cursor: 'pointer',
    color: colors.navy,
  },
  menu: {
    position: 'absolute',
    top: 'calc(100% + 10px)',
    left: -14,
    zIndex: 5,
    width: 300,
    display: 'flex',
    flexDirection: 'column',
    padding: '4px',
    border: `1px solid ${colors.border}`,
    borderRadius: '8px',
    background: '#FFFFFF',
    boxShadow:
      '0 12px 24px -4px rgba(145,158,171,.12), 0 0 2px rgba(145,158,171,.2)',
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  option: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 12px',
    border: 0,
    borderRadius: '6px',
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '13px',
    textAlign: 'left',
    color: colors.navy,
    '&:hover': {
      background: colors.hover,
    },
  },
  optionOn: {
    background: 'rgba(1,95,251,.08)',
    color: colors.primary,
  },
  optionLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  kind: {
    flex: 'none',
    fontSize: '11px',
    fontWeight: 500,
    color: colors.subtle,
  },
  note: {
    padding: '8px 12px',
    fontSize: '12px',
    lineHeight: 1.5,
    color: colors.muted,
  },
  manage: {
    marginTop: 4,
    padding: '8px 12px',
    border: 0,
    borderTop: '1px solid #F1F3F5',
    background: 'transparent',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '12px',
    fontWeight: 500,
    textAlign: 'left',
    color: colors.primary,
  },
})

const withoutProtocol = url => (url || '').replace(/^https?:\/\//, '')

/**
 * The globe in the portal URL bar: lists the addresses the portal opens on
 * (its custom domain and the default one) and switches the one shown.
 */
const PortalDomainPicker = ({
  addresses,
  value,
  hasCustomDomain,
  onChange,
  onManage,
}) => {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const onDown = event => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  return (
    <div className={classes.root} ref={ref}>
      <button
        type="button"
        title="Change domain"
        className={classes.trigger}
        onClick={() => setOpen(current => !current)}
      >
        <Icon icon="tabler:world" width={14} height={14} />
      </button>
      {open && (
        <div className={classes.menu}>
          {addresses.map(address => (
            <button
              key={address.url}
              type="button"
              className={`${classes.option} ${
                address.url === value ? classes.optionOn : ''
              }`}
              onClick={() => {
                onChange(address.url)
                setOpen(false)
              }}
            >
              <span className={classes.optionLabel}>
                {address.label || withoutProtocol(address.url)}
              </span>
              <span className={classes.kind}>{address.kind}</span>
            </button>
          ))}
          {!hasCustomDomain && (
            <div className={classes.note}>
              Use your own address, like learn.yourcompany.com.
            </div>
          )}
          <button type="button" className={classes.manage} onClick={onManage}>
            {hasCustomDomain ? 'Manage domains' : 'Add custom domain'}
          </button>
        </div>
      )}
    </div>
  )
}

export default PortalDomainPicker
